import { axiosGetIDPConfig } from "@/modules/hiring/stateManagement/api";
import { useEffect, useRef, useState, useCallback } from "react";

export type TokenResponse = {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  refresh_expires_in?: number;
  token_type: string;
  id_token?: string;
  session_state?: string;
  scope?: string;
};

const REFRESH_BUFFER_SECONDS = 300; // 5 minutes before expiry

function saveSession(t: TokenResponse) {
  console.log("saveSession");
  sessionStorage.setItem("hm_kc_token", JSON.stringify(t));
  sessionStorage.setItem("hm_auth_token", t.access_token);
}

function loadSession(): TokenResponse | null {
  const s = sessionStorage.getItem("hm_kc_token");
  console.log("loadSession");
  return s ? (JSON.parse(s) as TokenResponse) : null;
}

export function useKeycloak() {
  console.log("useKeycloak");
  const [token, setToken] = useState<TokenResponse | null>(() => loadSession());
  const timerRef = useRef<number | null>(null);
  const teamsTokenRef = useRef<string | null>(null);
  const [teamsToken, setTeamsToken] = useState<string | null>(null);


  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // --------------------------
  // Apply token helper (not memoized to avoid circular deps)
  // --------------------------
  const applyTokenInternal = (t: TokenResponse) => {
    console.log("applyToken");
    setToken(t);
    saveSession(t);
  };

  // --------------------------
  // REFRESH TOKEN (single endpoint)
  // --------------------------
  const refreshUsingBackend = useCallback(
    async (_teamsToken: string) => {
        console.log("refreshUsingBackend");
        try {
            const tokenResponse = await axiosGetIDPConfig(_teamsToken);
            applyTokenInternal(tokenResponse);
            // Schedule refresh after applying token
            clearTimer();
            const ms = Math.max((tokenResponse.expires_in - REFRESH_BUFFER_SECONDS) * 1000, 1000);
            timerRef.current = window.setTimeout(async () => {
              console.log("scheduleRefresh timeout", ms);
              await refreshUsingBackend(teamsTokenRef.current ?? '');
            }, ms);
            return tokenResponse;
        } catch (error) {
            console.error("Failed to refresh token", error);
            throw error;
        }
    },
    []
  );

  const scheduleRefresh = useCallback((t: TokenResponse) => {
    console.log("scheduleRefresh");
    clearTimer();
    const ms = Math.max((t.expires_in - REFRESH_BUFFER_SECONDS) * 1000, 1000);

    timerRef.current = window.setTimeout(async () => {
      console.log("scheduleRefresh timeout", ms);
      // Use ref to get the latest teamsToken value
      await refreshUsingBackend(teamsTokenRef.current ?? '');
    }, ms);
  }, [refreshUsingBackend]);

  const applyToken = useCallback((t: TokenResponse) => {
    applyTokenInternal(t);
    scheduleRefresh(t);
  }, [scheduleRefresh]);

  // --------------------------
  // INITIAL TOKEN (Teams token)
  // --------------------------
  const fetchInitialToken = useCallback(
    async (_teamsToken: string) => {
        console.log("fetchInitialToken");
        teamsTokenRef.current = _teamsToken;
        setTeamsToken(_teamsToken);
        try {       
       const tokenResponse = await axiosGetIDPConfig(_teamsToken);
       applyToken(tokenResponse);
       return tokenResponse;
    } catch (error) {
        console.error("Failed to get initial token", error);
        throw error;
    }     
    },
    [applyToken]
  );

  const manualRefresh = useCallback(async () => {
    console.log("manualRefresh");
    if (!token) return null;
    // Use ref to get the latest teamsToken value
    return await refreshUsingBackend(teamsTokenRef.current ?? '');
  }, [token, refreshUsingBackend]);

  useEffect(() => {
    return () => clearTimer();
  }, []);

  return {
    token,
    getAccessToken: () => token?.access_token ?? null,
    fetchInitialToken,
    manualRefresh,
  };
}
